import React, { useState } from "react";
import { Device } from "@app/shared/src/models";
import { Btn, Modal, Tag } from "@app/shared/src/components_v2";
import { siteLabel } from "../../helpers/deviceLabels";

/**
 * Confirms a change that reaches beyond one truck.
 *
 * A fleet default is applied by writing to every unit that was following the old value OF THE
 * SETTING THAT CHANGED — per setting, not per section. A truck that overrides the approach speed
 * but follows the fleet's horn pulse is a legitimate target of a horn-pulse change, and only its
 * horn pulse moves; its override is untouched. The copy below says so explicitly, because a
 * director who sees an "Overridden" truck in this list otherwise reads it as their override being
 * clobbered. The blast radius has to be visible before the click, not after: this names the count
 * and lists the trucks. Units set differently on every changed setting are shown as untouched.
 */
export const ApplyToFleetDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    title: string;
    /** What is changing, in one line — "Radar profile → Attenuator (fast approach)". */
    summary: React.ReactNode;
    targets: Device[];
    /** Units that keep their own value because they were already set differently. */
    untouched?: Device[];
    onConfirm: () => Promise<void>;
}> = ({ open, onClose, title, summary, targets, untouched = [], onConfirm }) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();

    const run = async () => {
        setBusy(true);
        setError(undefined);
        try {
            await onConfirm();
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not apply that change");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={busy ? () => undefined : onClose}
            title={title}
            subtitle={`Only the changed settings move, on ${targets.length} device${targets.length === 1 ? "" : "s"} still following their previous values`}
            footer={
                <>
                    <Btn tone="quiet" onClick={onClose} disabled={busy}>
                        Cancel
                    </Btn>
                    {/* Zero targets still saves: recording the fleet's intent is legitimate with no
                        unit to push to — a wiring rename, a provisioning address, a rule nothing
                        follows yet. Disabling here made those changes impossible to commit at all. */}
                    <Btn onClick={run} disabled={busy}>
                        {busy
                            ? targets.length === 0
                                ? "Saving…"
                                : `Applying to ${targets.length}…`
                            : targets.length === 0
                              ? "Save defaults"
                              : `Apply to ${targets.length} device${targets.length === 1 ? "" : "s"}`}
                    </Btn>
                </>
            }
        >
            <div className="rounded-[12px] border border-[var(--line)] bg-[var(--surface-2)] px-4 py-3 text-[13.5px] font-semibold">
                {summary}
            </div>

            <div>
                <div className="mb-2 flex items-center gap-2">
                    <Tag tone="accent">{targets.length}</Tag>
                    <span className="text-[12.5px] text-[var(--muted)]">
                        {targets.length === 1
                            ? "unit follows the previous value of a changed setting"
                            : "units follow the previous value of a changed setting"}
                        {" — each moves only on the settings it follows"}
                    </span>
                </div>
                <div className="max-h-[180px] overflow-y-auto rounded-[12px] border border-[var(--line)]">
                    {targets.length === 0 ? (
                        <p className="px-4 py-3 text-[12.5px] text-[var(--muted)]">No units are on the current value.</p>
                    ) : (
                        targets.map((d) => (
                            <div key={d.id} className="flex items-center gap-3 border-t border-[var(--line)] px-4 py-2.5 first:border-t-0">
                                {/* The TMA number, not the device row's name — it is the identity
                                    a director recognises; the key settles which box it is. */}
                                <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">{siteLabel(d)}</span>
                                <span className="font-mono text-[11.5px] text-[var(--muted)]">{d.deviceKey}</span>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {untouched.length > 0 && (
                <p className="text-[12px] leading-relaxed text-[var(--faint)]">
                    {untouched.length} unit{untouched.length === 1 ? " is" : "s are"} set differently on every changed
                    setting and will keep {untouched.length === 1 ? "its" : "their"} own values:{" "}
                    {untouched.slice(0, 4).map((d) => siteLabel(d)).join(" · ")}
                    {untouched.length > 4 ? " …" : ""}
                </p>
            )}

            {error && <p className="text-[12.5px] text-[var(--crit)]">{error}</p>}
        </Modal>
    );
};
